import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { MDRBadge } from "@/components/MDRBadge";
import {
  LayoutDashboard, Stethoscope, Receipt, Settings, BarChart3,
  Menu, ArrowLeftRight, LogOut, Hospital
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

const navItems = [
  { path: "/structure", label: "Panoramica", icon: LayoutDashboard, end: true },
  { path: "/structure/specialists", label: "Medici Specialisti", icon: Stethoscope },
  { path: "/structure/monitoring", label: "Monitoraggio", icon: BarChart3 },
  { path: "/structure/billing", label: "Rendicontazione", icon: Receipt },
  { path: "/structure/configuration", label: "Configurazione", icon: Settings }
];

export const StructureLayout = () => {
  const navigate = useNavigate();
  const { isAuthenticated, currentUser } = useAppContext();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const NavItem = ({ item, onClick }) => (
    <NavLink
      to={item.path}
      end={item.end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
          isActive ? "bg-primary text-primary-foreground shadow-md" : "text-muted-foreground hover:bg-muted hover:text-foreground"
        )
      }
    >
      <item.icon className="h-5 w-5" />
      {item.label}
    </NavLink>
  );

  const Sidebar = ({ onItemClick }) => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b px-4">
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </div>
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 border-2 border-cyan-200">
            <AvatarFallback className="bg-cyan-100 text-cyan-700 font-semibold">
              <Hospital className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-medium text-sm leading-tight truncate">{currentUser?.name || "Responsabile"}</p>
            <p className="text-xs text-muted-foreground">Struttura Accreditata</p>
          </div>
        </div>
        <MDRBadge variant="admin" size="sm" className="mt-3" />
      </div>
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map(item => <NavItem key={item.path} item={item} onClick={onItemClick} />)}
        </nav>
      </ScrollArea>
      <div className="border-t p-3 space-y-1">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
          onClick={() => navigate("/")}
          data-testid="structure-switch-profile-btn"
        >
          <ArrowLeftRight className="h-4 w-4" />
          Cambia profilo
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-destructive hover:text-destructive"
          onClick={() => navigate("/login")}
        >
          <LogOut className="h-4 w-4" />
          Esci
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r bg-card md:block">
        <Sidebar />
      </aside>
      <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-card px-4 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <Sidebar onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </header>
      <main className="md:pl-64">
        <div className="min-h-[calc(100vh-4rem)] md:min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default StructureLayout;
